import CT from '@marketplace-ai/tags';
import { ColDef, DoesFilterPassParams } from 'ag-grid-community';
import { ACTIONS_COLUMN_BASE, ActionsRenderer, type ActionDef } from '~/components/Table/gridUtil';
import { createDateCol } from '~/components/agGrid/columns/dateColumns';
import { createValidCol } from '~/components/agGrid/columns/sharedColumns';
import { CustomSetFilter } from '~/components/agGrid/filters';
import { COUNTRIES, STATES } from '../../../constants';
// [MCV-1574] `import type` so this stays a type-only reference. types.ts
// imports NON_PRACTISING_STATUSES (a value) from this file below; a plain
// value import here would make that a genuine circular runtime dependency.
import type { CreateMedLicenseFormValues, FilterSelect, MedLicenseFormValues } from './types';

const licenseStatuses = [
  'Active',
  'Deceased',
  'Expired',
  'Expired, Non-Disciplinary Condition',
  'Expired, Non-Disciplinary Restriction',
  'Expired, Probation',
  'Expired, Revoked',
  'Expired, Suspension',
  'Expired, Surrendered',
  'Finding',
  'Non-Disciplinary Condition',
  'Non-Disciplinary Restriction',
  'Void',
  'Probation',
  'Revoked',
  'Suspended',
  'Termination due to Board Action',
  'Surrendered',
  'Retired',
];

// [MCV-1574] Statuses that represent a licence which is not currently
// practising. Drives whether the Dates step shows/requires an inactive date
// + explanation, replacing the old manual "Is the License Inactive?" toggle
// that was completely independent of the selected status.
//
// The "Expired" family is derived with a prefix match, mirroring the
// isExpiredLabel check in types.ts, so a new "Expired, ..." variant added
// above is picked up automatically instead of needing a second, driftable
// list here. Everything else that means "not practising" is spelled out
// explicitly. Deliberately EXCLUDES Active, Probation, Non-Disciplinary
// Condition, Non-Disciplinary Restriction, and Finding -- those are
// restrictions on a still-active licence and must not prompt for an
// inactive date/explanation.
const OTHER_NON_PRACTISING_STATUSES = [
  'Suspended',
  'Revoked',
  'Retired',
  'Surrendered',
  'Void',
  'Deceased',
  'Termination due to Board Action',
];

export const NON_PRACTISING_STATUSES = licenseStatuses.filter(
  (status) => status.startsWith('Expired') || OTHER_NON_PRACTISING_STATUSES.includes(status)
);

export const FILTER_SELECTS: FilterSelect[] = [
  {
    type: 'status',
    label: 'Status',
    options: [
      { label: 'All', value: 'all' },
      {
        label: 'Expiring Soon',
        value: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0],
      },
      { label: 'Expired', value: new Date().toISOString().split('T')[0] },
    ],
    cyTag: CT.medicalLicenses.table.statusFilter,
  },
  {
    type: 'location',
    label: 'Location',
    options: [{ label: 'All Jurisdictions', value: 'all' }],
    cyTag: CT.medicalLicenses.table.locationFilter,
  },
  {
    type: 'expiration',
    label: 'Expiration',
    options: [{ label: 'All', value: 'all' }],
    cyTag: CT.medicalLicenses.table.expirationFilter,
  },
];

export const COLUMNS: ColDef[] = [];

export const createMedLicenseCols = ({
  handleDelete,
  handleEdit,
}: {
  handleDelete: (id: number) => void;
  handleEdit: (id: number) => void;
}): ColDef<MedLicenseFormValues>[] => [
  createValidCol(),
  {
    field: 'id',
    hide: true,
  },
  {
    headerName: 'License',
    field: 'licenseType.label',
    tooltipField: 'licenseType.label',
  },
  {
    headerName: 'Number',
    field: 'licenseNumber',
  },
  {
    // MCV-829: an explicit colId is required when a column has no `field`
    // path. Without it, ag-grid auto-assigns a numeric colId ("0", "1",
    // ...) and places the column at a non-deterministic DOM position
    // because numeric-id columns get processed AFTER named-id columns
    // during column registration. The DOM order then no longer matches
    // the columnDefs declaration order, which breaks position-based
    // queries like `cy.get('.ag-header-cell-text').eq(N)` in e2e tests.
    // Latent on main; surfaced by this PR's $_LOAD_VAULT_DATA step
    // changing render timing.
    colId: 'location',
    headerName: 'Location',
    valueGetter: ({ data }) => {
      if (data) {
        return `${data.stateCode?.label}, ${data.countryCode.label}`;
      }
    },
    filter: {
      component: CustomSetFilter,
      doesFilterPass: ({ model, node }: DoesFilterPassParams) => {
        if (!model || model.length === 0) return true;
        const location = `${node.data.stateCode?.label}, ${node.data.countryCode.label}`;
        return model.includes(location);
      },
    },
  },
  {
    headerName: 'Status',
    field: 'currStatus.label',
    filter: {
      component: CustomSetFilter,
      doesFilterPass: ({ model, node }: DoesFilterPassParams) => {
        if (!model || model.length === 0) return true;
        return model.includes(node.data.currStatus.label);
      },
    },
  },
  createDateCol('expiryDate', 'Expiration Date'),
  {
    ...ACTIONS_COLUMN_BASE,
    width: 80,
    cellRenderer: ActionsRenderer,
    cellRendererParams: {
      actions: [
        { type: 'edit', handler: handleEdit, cyTag: CT.medicalLicenses.table.editBtn },
        { type: 'delete', handler: handleDelete, cyTag: CT.medicalLicenses.table.deleteBtn },
      ] satisfies ActionDef[],
    },
  },
];

export const STATUS_OPTIONS = licenseStatuses.map((status) => ({ label: status, value: status }));

export const emptyForm: CreateMedLicenseFormValues = {
  licenseType: { value: '', label: '' },
  licenseNumber: '',
  origIssueDate: '',
  renewalDate: '',
  expiryDate: '',
  currStatus: { value: '', label: '' },
  inactiveDate: '',
  inactiveExplainTx: '',
  stateCode: STATES[0],
  countryCode: COUNTRIES.find((country) => country.value === 'US') || COUNTRIES[0],
  warnings: {},
  validFlag: true,
};
